import React, { type FC, Fragment, type PropsWithChildren, type ReactElement } from 'react'
import { Redirect as BaseRedirect, Route as BaseRoute, Switch as BaseSwitch } from 'react-router-dom'

import { assert, isArray, type ReadonlyNonEmptyArray } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

import type { FCProps, ReadonlyPropsWithChildren } from '../utils'
import { getRouteChildren, getRouteConfig, getRouteInternalPath } from './Config'
import {
    type AbsoluteRouteOnlyPath,
    type AbsoluteRoutePath,
    type AbsoluteRoutePathWithId,
    type AbsoluteRoutePathWithServiceAndResourceId,
    type AbsoluteRoutePathWithServiceId,
    isAbsoluteRouteOnlyPath,
    type RouterProps,
} from './Domain'
import { useHasCapabilities } from './Hook.HasCapabilities'
import { ConnectwareRoute, ConnectwareRouter } from './Router.Internal'

type CybusRouterProps = Readonly<{
    /**
     * Navigations components to be rendered (through injection) inside the router
     */
    appNavigation?: FC<PropsWithChildren>

    /**
     * Component that will be rendered again and again for each route
     */
    routeNavigation?: FC<PropsWithChildren>

    /**
     * Component that will be rendered if user is denied access
     */
    deniedAccess?: RouterProps['deniedAccess']

    /**
     * Where the application will start
     */
    initial: AbsoluteRouteOnlyPath

    /**
     * In case you get redirected elsewhere
     */
    onNotFound?: AbsoluteRouteOnlyPath

    /**
     * Only expect routes or switches as children
     */
    children:
        | readonly ReactElement<FCProps<typeof Route> | FCProps<typeof SwitchingRoute>>[]
        | ReactElement<FCProps<typeof Route> | FCProps<typeof SwitchingRoute>>

    /**
     * If in strict mode the childr elements passed to this element will be validated
     * If the validation fails then a rendering error will be thrown
     *
     * Setting to false is useful in tests where one needs to have the component inside a router
     * But not necessarily authenticated to see a particular route
     *
     * @default true
     */
    strict?: boolean
}>

export const Redirect: FC<
    ReadonlyPropsWithChildren<{
        to: AbsoluteRoutePath
        /**
         * If redirecting is not possible due to user lacking permissions, then attempt to redirect to another sibling
         */
        fallbackToSiblings?: boolean
    }>
> = ({ to, fallbackToSiblings }) => {
    const hasCapabilities = useHasCapabilities()
    const fallbackParent = fallbackToSiblings && getRouteConfig(to).parent

    /**
     * If there is a parent to fallback to, then check permissions of any of the pages under it
     */
    const route = (fallbackParent && [to, ...getRouteChildren(fallbackParent)].find(hasCapabilities)) || to

    return <BaseRedirect to={getRouteInternalPath(route)} />
}

export const Route: FC<
    ReadonlyPropsWithChildren<
        | { path: AbsoluteRoutePath }
        | {
              paths:
                  | readonly AbsoluteRouteOnlyPath[]
                  | readonly (AbsoluteRoutePathWithServiceId | AbsoluteRoutePathWithId | AbsoluteRoutePathWithServiceAndResourceId)[]
          }
    >
> = () => <Fragment />

export const SwitchingRoute: FC<
    Readonly<{
        /**
         * Prefix rendered before every route
         */
        prefix: FC
        children: ReadonlyNonEmptyArray<ReactElement<FCProps<typeof Route>>>
    }>
> = () => <Fragment />

const DefaultRouteNavigation: FC<PropsWithChildren> = ({ children }) => <>{children}</>

const isChildOfType = <Props,>(child: ReactElement<unknown>, Component: FC<Props>): child is ReactElement<Props> => child.type === Component
const areChildrenOfType = <Props,>(children: readonly ReactElement<unknown>[], Component: FC<Props>): children is readonly ReactElement<Props>[] =>
    children.every((c) => isChildOfType(c, Component))
const resolvePaths = (c: ReactElement<FCProps<typeof Route>>): readonly AbsoluteRoutePath[] => ('paths' in c.props ? c.props.paths : [c.props.path])

export const Switch: FC<Omit<CybusRouterProps, 'appNavigation' | 'deniedAccess' | 'initial'>> = ({
    routeNavigation: RouteNavigation = DefaultRouteNavigation,
    onNotFound,
    strict,
    children,
}) => (
    /**
     * Don't try to move this to another place
     * The ('react-router-dom').Route needs to be exposed directly to the Switch
     */
    <BaseSwitch>
        {(isArray(children) ? children : [children]).map((child, k) => {
            if (isChildOfType(child, Route)) {
                const paths = resolvePaths(child)

                return (
                    <BaseRoute
                        key={k}
                        path={paths.map(getRouteInternalPath)}
                        exact={paths.some(isAbsoluteRouteOnlyPath)}
                        render={({ match }) => (
                            <RouteNavigation>
                                <ConnectwareRoute path={paths.find((p) => getRouteInternalPath(p) === match.path)!}>{child.props.children}</ConnectwareRoute>
                            </RouteNavigation>
                        )}
                    />
                )
            }

            if (isChildOfType(child, SwitchingRoute) && areChildrenOfType(child.props.children, Route)) {
                const paths = child.props.children.flatMap(resolvePaths)
                const Prefix = child.props.prefix

                return (
                    <BaseRoute key={k} path={paths.map(getRouteInternalPath)} exact={paths.some(isAbsoluteRouteOnlyPath)}>
                        <RouteNavigation>
                            <Prefix />
                            <Switch>{child.props.children}</Switch>
                        </RouteNavigation>
                    </BaseRoute>
                )
            }

            /**
             * If you are seeing this error you either:
             *
             * - Are doing something very wrong by passing unsupported child elements to the router/switcher
             * - Forgot to set strict to false in one of your unit tests
             */
            assert(
                !strict,
                new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unsupported child found while running in strict mode', {
                    supported: [Route.name, SwitchingRoute.name],
                })
            )

            return <Fragment key={k}>{child}</Fragment>
        })}
        {/* Fallback */}
        {onNotFound && <Redirect to={onNotFound}>{children}</Redirect>}
    </BaseSwitch>
)

export const CybusRouter: FC<CybusRouterProps> = ({
    appNavigation: AppNavigation = Fragment,
    deniedAccess = Fragment as RouterProps['deniedAccess'],
    strict = true,
    initial,
    ...props
}) => (
    <ConnectwareRouter initial={initial} deniedAccess={deniedAccess}>
        <AppNavigation>
            <Switch {...props} strict={strict} />
        </AppNavigation>
    </ConnectwareRouter>
)
